import SetOrderDetails from "components/order/RateOrder/SetOrderDetails";
import SetOrderHeader from "components/order/SetOrderHeader";
import SetOrderFooter from "components/order/SetOrderFooter";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { useEffect, useCallback, useMemo } from "react";
import { useOneAgorder, useCreateOrder, useUpdateAgorder } from "http-hooks/agorder/useAgorder";
import { useOrderHeader } from "hooks/order";
import useAuth from "hooks/useAuth";
import useCompanyPreferences from "hooks/useCompanyPreferences";
import useFullScreenLayout from "hooks/useFullScreenLayout";
import { getFormType, getOrdTypFromFormType } from "utils/orderHelpers";

export default function RateOrder() {
  useFullScreenLayout();

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { agttp } = useAuth();
  const { isOrderEntryEnabled } = useCompanyPreferences();

  // Get formtype from query param (G/P/S)
  const formtype = searchParams.get('formtype');
  const formType = useMemo(() => getFormType(agttp, formtype), [agttp, formtype]);
  const ordTyp = useMemo(() => getOrdTypFromFormType(agttp, formtype), [agttp, formtype]);
  
  // Get orderId from location.state or query parameters
  const id = useMemo(() => {
    if (location.state?.id) {
      return location.state.id;
    }
    return searchParams.get('orderId');
  }, [location.state?.id, searchParams]);

  // Global order header state
  const { 
    orderHeader, 
    setOrderHeader,
    updateOrderHeader,
    lotData, 
    setLotData,
    rateOrderItems,
    setRateOrderItems,
    populateRateOrderItemsFromOrder,
    updateSelectBox,
    setPricelistFilters,
    setProtectedCases
  } = useOrderHeader();

  const orderEntryBlocked = !isOrderEntryEnabled();

  // Determine if we need to fetch order items (when editing an existing order)
  const shouldFetchItems = !!id;
  
  const { data, isLoading: isOrderLoading } = useOneAgorder(id, !!id && !orderEntryBlocked, { 
    show_items: shouldFetchItems 
  });

  // Create order mutation
  const createOrderMutation = useCreateOrder({});

  // Update order mutation
  const updateOrderMutation = useUpdateAgorder({});

  // Load all order data into global store when available
  useEffect(() => {
    if (data) {
      setOrderHeader({
        custom_ordno: data.custom_ordno || "",
        ordno: data.ordno || "",
        ordate: data.ordate,
        ordertyp: data.ordertyp || "G",
        ordtyp: data.ordtyp || ordTyp,
        ordcusid: data.ordcusid,
        ordvendid: data.ordvendid,
        ordtptid: data.ordtptid,
        orddespatch: data.orddespatch,
        orderedby: data.orderedby,
        pldate: data.pldate,
        prntp: data.prntp,
        is_scheme: data.is_scheme || 0,
        agent_salesman_code: data.agent_salesman_code || "",
        private_marka: data.private_marka || "",
        ordremark1: data.ordremark1 || "",
        ordremark2: data.ordremark2 || "",
      });

      // Set dropdown selections using populated data
      if (data.customer) {
        updateSelectBox('customer', data.customer);
      }
      
      if (data.vendor) {
        updateSelectBox('vendor', data.vendor);
        // Set pricelist filters for vendor - always set this for proper pricelist loading
        setPricelistFilters({ supid: data.ordvendid });
      }
      
      if (data.transporter) {
        updateSelectBox('transporter', data.transporter);
      }

      // Set agent/salesman selection if available
      if (data.agentSalesman) {
        updateSelectBox('agentSalesman', data.agentSalesman);
      }

      // Set pricelist selection if pricelist data is available
      if (data.pricelist) {
        updateSelectBox('pricelist', data.pricelist);
      }

      // Set print type selection if print type data is available
      if (data.printType) {
        updateSelectBox('printType', data.printType);
      }

      // Set protected cases (billed/closed) for readonly display
      if (data.protectedCases) {
        setProtectedCases(data.protectedCases);
      }

      // Populate lot data from order details
      if (data.orderDetails && data.orderDetails.length > 0) {
        const lotMap = new Map();
        
        // Extract unique case information
        data.orderDetails.forEach(detail => {
          const caseId = detail.caseno;
          if (!lotMap.has(caseId)) {
            lotMap.set(caseId, {
              caseId: caseId,
              lotQty: detail.lotqty || 1,
              remark: detail.remarklot || ""
            });
          }
        });
        
        // Convert map to array and set lot data
        const lotDataArray = Array.from(lotMap.values());
        setLotData(lotDataArray);
      }

      // Populate rate order items if order details are available (when editing)
      if (data.orderDetails && data.orderDetails.length > 0) {
        populateRateOrderItemsFromOrder(data.orderDetails);
      }
    }
  }, [data, setOrderHeader, updateSelectBox, setPricelistFilters, populateRateOrderItemsFromOrder, setLotData, setProtectedCases]);

  // Reset to default rate order items when creating a new order and set ordtyp
  useEffect(() => {
    if (!id) {
      setRateOrderItems([
        {
          case_id: 1,
          itemname: null,
          rate: "",
          UOM: "",
          packratio: "",
          quantity: "",
          itemdetails: "",
        }
      ]);
      setProtectedCases([]);
      updateOrderHeader({ ordtyp: ordTyp });
    }
  }, [id, setRateOrderItems, setProtectedCases, ordTyp, updateOrderHeader]);

  const handleSave = useCallback(async () => {
    try {
      // Filter and format rate order items with PCS conversion
      const filteredRateOrderItems = rateOrderItems
        .filter(item => item.itemname && item.quantity && parseFloat(item.quantity) > 0)
        .map(item => {
          const quantity = parseFloat(item.quantity) || 0;
          const aguomconval = parseFloat(item.itemname?.conval) || 1; // Get conversion value from selected item
          const quantityInPCS = quantity * aguomconval; // Convert to PCS
          
          return {
            itemId: item.itemname?.value,
            caseId: item.case_id || 1,
            quantity: quantity, // Original quantity as entered by user
            quantityPcs: quantityInPCS, // Converted PCS quantity
            rate: parseFloat(item.rate) || 0,
            itemDetails: item.itemdetails || ""
          };
        });

      // Prepare order data directly
      const orderData = {
        orderHeader,
        items: filteredRateOrderItems,
        lotData,
      };

      // Submit the order (create or update based on id)
      if (id) {
        // Edit mode - use update mutation
        await updateOrderMutation.mutateAsync({ id, data: orderData });
      } else {
        // Create mode - use create mutation
        await createOrderMutation.mutateAsync(orderData);
        // Navigate to orders page after successful creation
        navigate('/order');
      }
    } catch (error) {
      console.error("Order save failed:", error);
    }
  }, [id, orderHeader, rateOrderItems, lotData, createOrderMutation, updateOrderMutation, navigate]);

  // Block order entry if FY order entry is disabled
  if (orderEntryBlocked) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-12">
        <div className="rounded-md bg-yellow-50 border border-yellow-200 p-6 text-center">
          <h2 className="text-lg font-semibold text-yellow-800">Order Entry Blocked</h2>
          <p className="mt-2 text-sm text-yellow-700">
            Order entry for this Financial Year is blocked. Please contact your administrator.
          </p>
          <button
            type="button"
            onClick={() => navigate('/order')}
            className="mt-4 rounded-md bg-yellow-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-yellow-500"
          >
            Go to Orders
          </button>
        </div>
      </div>
    );
  }

  // Show loading state while fetching order data
  if (id && isOrderLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="animate-pulse">
          {/* Header skeleton */}
          <div className="mb-8">
            <div className="h-8 bg-gray-300 rounded w-1/3 mb-4"></div>
            <div className="grid grid-cols-3 gap-6">
              {Array.from({ length: 9 }).map((_, index) => (
                <div key={index} className="space-y-2">
                  <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                  <div className="h-10 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Items table skeleton */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-gray-300 px-6 py-3">
              <div className="h-6 bg-gray-400 rounded w-1/4"></div>
            </div>
            <div className="p-6 space-y-4">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="grid grid-cols-8 gap-4">
                  {Array.from({ length: 8 }).map((_, colIndex) => (
                    <div key={colIndex} className="h-10 bg-gray-200 rounded"></div>
                  ))}
                </div>
              ))}
            </div>
          </div>
          
          {/* Footer skeleton */}
          <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex justify-between items-center">
              <div className="h-4 bg-gray-300 rounded w-1/4"></div>
              <div className="h-10 bg-gray-300 rounded w-24"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SetOrderHeader 
        id={id} 
        title="Order Form (With RATE)"
        isLoading={isOrderLoading}
        formType={formType}
      />
      <SetOrderDetails />
      <SetOrderFooter
        onSave={handleSave}
        isLoading={createOrderMutation.isPending || updateOrderMutation.isPending}
      />
    </div>
  );
}
